import React, { useState, useMemo } from 'react';
import '../../css/Calendar.css';

interface DateRangePickerProps {
  startDate: string;
  endDate: string;
  onDateChange: (startDate: string, endDate: string) => void;
  onClose: () => void;
}

const DateRangePicker: React.FC<DateRangePickerProps> = ({
  startDate,
  endDate,
  onDateChange,
  onClose,
}) => {
  const [currentMonth, setCurrentMonth] = useState(() => {
    const date = startDate ? new Date(startDate) : new Date();
    date.setDate(1);
    return date;
  });
  const [selectionMode, setSelectionMode] = useState<'single' | 'range'>('range');
  const [tempStartDate, setTempStartDate] = useState<string | null>(startDate || null);
  const [tempEndDate, setTempEndDate] = useState<string | null>(endDate || null);
  const [selectingStart, setSelectingStart] = useState(true);

  const monthLabel = useMemo(() => {
    return currentMonth.toLocaleString('default', { month: 'long', year: 'numeric' });
  }, [currentMonth]);

  const calendarDays = useMemo(() => {
    const firstDayOfMonth = new Date(currentMonth);
    const start = new Date(firstDayOfMonth);
    const day = start.getDay();
    start.setDate(start.getDate() - day);

    const cells: Date[] = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      cells.push(d);
    }
    return cells;
  }, [currentMonth]);

  const localDateKey = (d: Date): string => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const da = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${da}`;
  };

  const todayKey = useMemo(() => localDateKey(new Date()), []);

  const isDateInRange = (dateKey: string): boolean => {
    if (!tempStartDate) return false;
    if (selectionMode === 'single') {
      return dateKey === tempStartDate;
    }
    if (!tempEndDate) {
      return dateKey === tempStartDate;
    }
    return dateKey >= tempStartDate && dateKey <= tempEndDate;
  };

  const isStartDate = (dateKey: string): boolean => {
    return dateKey === tempStartDate;
  };

  const isEndDate = (dateKey: string): boolean => {
    return dateKey === tempEndDate;
  };

  const handleDateClick = (dateKey: string) => {
    if (selectionMode === 'single') {
      setTempStartDate(dateKey);
      setTempEndDate(dateKey);
      onDateChange(dateKey, dateKey);
      setTimeout(() => onClose(), 300);
    } else {
      if (selectingStart || !tempStartDate || dateKey < tempStartDate) {
        setTempStartDate(dateKey);
        setTempEndDate(null);
        setSelectingStart(false);
      } else {
        setTempEndDate(dateKey);
        setSelectingStart(true);
        onDateChange(tempStartDate, dateKey);
      }
    }
  };

  const handleApply = () => {
    if (tempStartDate) {
      onDateChange(tempStartDate, tempEndDate || tempStartDate);
      onClose();
    }
  };

  const handleClear = () => {
    setTempStartDate(null);
    setTempEndDate(null);
    setSelectingStart(true);
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ width: '600px', maxWidth: '90vw' }}>
        <button className="close-btn" onClick={onClose}>&times;</button>
        <h2>Select Date Range</h2>

        <div style={{ marginBottom: '20px', display: 'flex', gap: '10px', alignItems: 'center' }}>
          <label style={{ fontWeight: 'bold' }}>Mode:</label>
          <button
            onClick={() => {
              setSelectionMode('single');
              setSelectingStart(true);
            }}
            style={{
              padding: '6px 12px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              background: selectionMode === 'single' ? '#2196f3' : '#fff',
              color: selectionMode === 'single' ? '#fff' : '#333',
              cursor: 'pointer'
            }}
          >
            Single Date
          </button>
          <button
            onClick={() => {
              setSelectionMode('range');
              setSelectingStart(true);
            }}
            style={{
              padding: '6px 12px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              background: selectionMode === 'range' ? '#2196f3' : '#fff',
              color: selectionMode === 'range' ? '#fff' : '#333',
              cursor: 'pointer'
            }}
          >
            Date Range
          </button>
        </div>

        <div style={{ marginBottom: '20px', padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
          {tempStartDate && (
            <div style={{ marginBottom: '8px' }}>
              <strong>Start:</strong> {new Date(tempStartDate).toLocaleDateString()}
            </div>
          )}
          {tempEndDate && tempEndDate !== tempStartDate && (
            <div>
              <strong>End:</strong> {new Date(tempEndDate).toLocaleDateString()}
            </div>
          )}
          {!tempStartDate && (
            <div style={{ color: '#666' }}>
              {selectionMode === 'single' ? 'Click a date to select' : 'Click a date to start selection'}
            </div>
          )}
        </div>

        <div className="calendar-grid" style={{ marginBottom: '20px' }}>
          <div className="calendar-controls" style={{ marginBottom: '15px', justifyContent: 'space-between' }}>
            <button
              onClick={() => setCurrentMonth(m => { const d = new Date(m); d.setMonth(m.getMonth() - 1); return d; })}
              style={{ border: '1px solid #ddd', background: '#fff', padding: '6px 10px', borderRadius: '4px', cursor: 'pointer' }}
            >
              ‹
            </button>
            <div className="month-label">{monthLabel}</div>
            <button
              onClick={() => setCurrentMonth(m => { const d = new Date(m); d.setMonth(m.getMonth() + 1); return d; })}
              style={{ border: '1px solid #ddd', background: '#fff', padding: '6px 10px', borderRadius: '4px', cursor: 'pointer' }}
            >
              ›
            </button>
          </div>

          <div className="calendar-weekdays">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
              <div key={d} className="calendar-weekday">{d}</div>
            ))}
          </div>

          <div className="calendar-cells" style={{ gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px' }}>
            {calendarDays.map((d, idx) => {
              const key = localDateKey(d);
              const inMonth = d.getMonth() === currentMonth.getMonth();
              const isToday = key === todayKey;
              const inRange = isDateInRange(key);
              const isStart = isStartDate(key);
              const isEnd = isEndDate(key);

              return (
                <div
                  key={idx}
                  onClick={() => handleDateClick(key)}
                  style={{
                    minHeight: '40px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    padding: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    backgroundColor: inRange ? '#dbeafe' : inMonth ? '#fff' : '#f9fafb',
                    borderColor: isStart || isEnd ? '#2196f3' : isToday ? '#60a5fa' : '#e5e7eb',
                    borderWidth: isStart || isEnd ? '2px' : '1px',
                    fontWeight: isStart || isEnd ? 'bold' : isToday ? '600' : 'normal',
                    color: !inMonth ? '#9ca3af' : isStart || isEnd ? '#2196f3' : isToday ? '#1d4ed8' : '#111827',
                    position: 'relative'
                  }}
                  title={key}
                >
                  {d.getDate()}
                  {(isStart || isEnd) && (
                    <div style={{
                      position: 'absolute',
                      top: '2px',
                      right: '2px',
                      width: '6px',
                      height: '6px',
                      borderRadius: '50%',
                      backgroundColor: '#2196f3'
                    }} />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="modal-actions">
          <button type="button" onClick={handleClear} className="secondary">
            Clear
          </button>
          <button type="button" onClick={onClose} className="secondary">
            Cancel
          </button>
          <button type="button" onClick={handleApply} className="primary" disabled={!tempStartDate}>
            Apply
          </button>
        </div>
      </div>
    </div>
  );
};

export default DateRangePicker;
